import React, { FC, useState, useEffect, useCallback } from 'react';
import { Modal, Steps, Button, Form, Input, InputNumber, Space, Descriptions, Tag, Result, Spin, Flex, App, Tabs, List, Empty, Upload, notification, UploadProps, Checkbox, Table } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, BarcodeOutlined, EyeOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WLocation, CountAddProps, CountAddItem, CountOrder, CountRes, BarcodeDiff, CountOrderItem, CountOrderStateEnum } from '@/types/inventoryV2';
import { countAdd, countDiff, countDo, countFetch, countItemRms, countItemAdds, countDel } from '@/apis/inventoryv2';
import SText from '@/styles/SText';
import errorCodes from '@/apis/error';
import useLabelColor from '@/pages/Warehouse/pages/InventoryManage/hooks/useLabelColor';
import BarcodeEditModal from '@/pages/Warehouse/pages/Inventory/components/InboundOrderForm/BarcodeEditModal';
import { generateTemplate, parseData } from './utils';
import STableLayout from '@/styles/STableLayout';
import LocationSelectContent from '@/components/Selector/LocationSelectContent';
import BarcodeDiffModal from './BarcodeDiffModal';
import { generateLotV2 } from '@/utils/codeParse';

export interface EditableTableItem {
  lotStr?: string;
  productCode?: string;
  counting?: number | null;
  barcodes?: string[];
}

interface CountAddV2Props {
  open: boolean;
  onClose: (delOrder?: CountOrder) => void;
  onSuccess?: (order: CountOrder, type: "UPDATE" | "ADD") => void;
  countId?: string;
}

const CountAddV2: FC<CountAddV2Props> = ({ open, onClose, onSuccess, countId }) => {
  const { t } = useTranslation();
  const { getLabelColor } = useLabelColor();
  const { modal } = App.useApp();

  const [current, setCurrent] = useState<number>(0);
  const [selectedLocation, setSelectedLocation] = useState<WLocation | null>(null);
  const [form] = Form.useForm();
  const [countOrder, setCountOrder] = useState<CountOrder | null>(null);
  const [diffData, setDiffData] = useState<CountRes[]>([]);
  const [loading, setLoading] = useState(false);
  const [barcodeModalInfo, setBarcodeModalInfo] = useState<{
    open: boolean;
    fieldIndex: number | null;
    barcodes: string[];
    maxCount?: number;
  }>({ open: false, fieldIndex: null, barcodes: [], maxCount: undefined });
  const [originalItems, setOriginalItems] = useState<CountOrderItem[]>([]);
  const [formChanged, setFormChanged] = useState(false);
  const [barcodeDiffModal, setBarcodeDiffModal] = useState<{
    open: boolean;
    barcodeDiffs: BarcodeDiff[];
    productCode: string;
    lotStr: string;
  }>({ open: false, barcodeDiffs: [], productCode: '', lotStr: '' });

  const [tableData, setTableData] = useState<EditableTableItem[]>([{}]);
  const [noLotChecked, setNoLotChecked] = useState<0 | 1>(0);
  const [uploading, setUploading] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      if (countId) {
        loadExistingCount();
      } else {
        setCurrent(0);
        setSelectedLocation(null);
        form.resetFields();
        setCountOrder(null);
        setDiffData([]);
        setOriginalItems([]);
        setTableData([{}]);
        setFormChanged(false);
      }
    }
  }, [open, countId]);

  const handleCellChange = (index: number, field: keyof EditableTableItem, value: any) => {
    const newData = [...tableData];
    if (index >= 0 && index < newData.length) {
      newData[index] = { ...newData[index], [field]: value };
      setTableData(newData);
      form.setFieldValue('items', newData);
      setFormChanged(true);
    }
  };

  const handleAddRow = () => {
    setTableData([...tableData, {}]);
    form.setFieldValue('items', [...tableData, {}]);
    setFormChanged(true);
  };

  const handleDeleteRow = (index: number) => {
    if (tableData.length > 1) {
      const newData = tableData.filter((_, i) => i !== index);
      setTableData(newData);
      form.setFieldValue('items', newData);
      setFormChanged(true);
    }
  };

  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        const validExistingData = tableData.filter(d => !!d.lotStr || !!d.productCode || !!d.counting);
        const newData = [...validExistingData, ...addList];
        setTableData(newData);
        form.setFieldValue('items', newData);
        setFormChanged(true);
        notification.success({ placement: 'top', message: t("counting.parseDataSuccess", { count: addList.length }) });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("counting.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false;
  };

  const loadExistingCount = async () => {
    try {
      setLoading(true);
      const order = await countFetch(countId!);
      setCountOrder(order);
      setSelectedLocation(order.location!);
      setOriginalItems(order.items || []);

      const formItems = order.items.map(item => ({
        lotStr: item.lotStr,
        productCode: item.productCode,
        counting: item.counting,
        barcodes: item.barcodes?.map(b => b.barcode) || [],
      }));
      form.setFieldsValue({
        desc: order.desc || '',
        items: formItems
      });

      setTableData(formItems);
      setCurrent(1);
      setFormChanged(false);
    } catch (error: any) {
      notification.error({ placement: 'top', message: errorCodes[error] || error.message || t('counting.loadFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (_: any = {}, isTempSave?: boolean) => {
    let noLot = tableData.find(t => !!!t.lotStr || !!!t.productCode || !!!t.counting);
    if (!!noLot) return notification.error({
      message: t('counting.subErr')
    })
    try {
      setLoading(true);
      const items: CountAddItem[] = tableData.filter(item => item.productCode).map((item: any) => ({
        lotStr: item?.lotStr || '',
        productCode: item.productCode,
        counting: item.counting?.toString() || '0',
        barcodes: item?.barcodes || [],
      }));
      if (countOrder) {
        if (formChanged) {
          const itemIds = originalItems.map(item => item.id);
          if (itemIds.length > 0) {
            await countItemRms(countOrder.id, itemIds);
          }

          const updatedOrder = await countItemAdds(countOrder.id, items);
          setCountOrder(updatedOrder);
          setOriginalItems(updatedOrder.items || []);

          notification.success({ placement: 'top', message: t('counting.itemsUpdated') });
        }

        if (!isTempSave) {
          const diffResult = await countDiff(countOrder.id);
          setDiffData(diffResult);
          setCurrent(2);
        }
        setFormChanged(false);
        return countOrder;
      } else {
        const formValues = form.getFieldsValue();
        const addData: CountAddProps = {
          locationId: selectedLocation!.id,
          desc: formValues.desc || '',
          items,
        };

        const result = await countAdd(addData);
        setCountOrder(result);
        setOriginalItems(result.items || []);

        if (!isTempSave) {
          const diffResult = await countDiff(result.id);
          setDiffData(diffResult);
          setCurrent(2);
          setFormChanged(false);
          notification.success({ placement: 'top', message: t('counting.addSuccess'), description: `${t('counting.num')}:  ${result.number}` });
        } else {
          notification.success({ placement: 'top', message: t('counting.tempSaveSuccess'), description: `${t('counting.num')}:  ${result.number}` });
        }
        return result;
      }
    } catch (error: any) {
      if (error === 73003) {
        notification.error({ placement: 'top', message: t('counting.addFailed'), description: t('counting.preOutExt') });
      } else if (error.code === 70605) {
        notification.error({ placement: 'top', message: t('counting.addFailed'), description: t("counting.noCode", { code: error?.extra ?? '' }) })
      }
      else notification.error({ placement: 'top', message: t('counting.addFailed') });
    } finally {
      setLoading(false);
    }
  };

  const handleTempSave = () => {
    modal.confirm({
      title: t('counting.tempSave'),
      content: t('counting.tempSaveConfirm'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await form.validateFields();
          const result = await handleFormSubmit({}, true);

          if (result && onSuccess) {
            onSuccess(result, countId ? "UPDATE" : "ADD");
          }

          onClose();
        } catch (error: any) {
          if (error?.errorFields) {
            notification.error({ placement: 'top', message: t('common.validateFailed') });
          }
        }
      }
    });
  };

  const handleNext = async () => {
    if (current === 0) {
      if (!selectedLocation) {
        notification.warning({ placement: 'top', message: t('counting.pleaseSelectLocation') });
        return;
      }
      setCurrent(1);
    } else if (current === 1) {
      form.submit();
    } else if (current === 2) {
      if (!countOrder) {
        notification.error({ placement: 'top', message: t('counting.noCountOrder') });
        return;
      }

      setLoading(true);
      try {
        await countDo(countOrder.id, noLotChecked);
        setCurrent(3);
        notification.success({ placement: 'top', message: t('counting.executeSuccess') });
      } catch (error: any) {
        console.error('Error executing count:', error);
        notification.error({ placement: 'top', message: errorCodes[error] || error.message || t('counting.executeFailed') });
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePrev = () => {
    if (current > 0) {
      if (current === 1 && !!countOrder) return
      setCurrent(current - 1);
    }
  };

  const handleClose = () => {
    if (current === 3) {
      onClose();
      if (onSuccess && countOrder) {
        onSuccess({
          ...countOrder,
          orderState: CountOrderStateEnum.DONE
        }, !!countId ? "UPDATE" : "ADD");
      }
    } else if (current === 1) {
      const values = form.getFieldValue('items');

      if (countId) {
        if (!formChanged) {
          onClose();
        } else {
          modal.confirm({
            title: t('counting.confirmClose'),
            icon: <ExclamationCircleOutlined />,
            content: t('counting.confirmCloseContent'),
            okText: t('common.save'),
            cancelText: t('common.notSave'),
            onOk: async () => {
              try {
                await form.validateFields();

                setLoading(true);
                const items: CountAddItem[] = tableData.filter(item => item.productCode).map((item: any) => ({
                  lotStr: item.lotStr || '',
                  productCode: item.productCode,
                  counting: item.counting?.toString() || '0',
                  barcodes: item.barcodes || [],
                }));

                if (countOrder) {
                  const itemIds = originalItems.map(item => item.id);
                  if (itemIds.length > 0) {
                    await countItemRms(countOrder.id, itemIds);
                  }
                  const updatedOrder = await countItemAdds(countOrder.id, items);
                  notification.success({ placement: 'top', message: t('counting.itemsUpdated') });
                  onClose();
                  onSuccess && onSuccess(updatedOrder, "UPDATE");
                }
                setLoading(false);
              } catch (error: any) {
                setLoading(false);
                if (error === 73003) {
                  notification.error({ placement: 'top', message: t('counting.addFailed'), description: t('counting.preOutExt') });
                } else if (error?.errorFields) {
                  notification.error({ placement: 'top', message: t('common.validateFailed') });
                } else {
                  notification.error({ placement: 'top', message: t('counting.saveFailed') });
                }
              }
            },
            onCancel: () => {
              onClose();
            }
          });
        }
      } else {
        const hasData = values && values.length > 0;

        if (!hasData) {
          onClose();
        } else {
          modal.confirm({
            title: t('counting.confirmClose'),
            icon: <ExclamationCircleOutlined />,
            content: t('counting.confirmCloseContent'),
            okText: t('common.save'),
            cancelText: t('common.notSave'),
            onOk: async () => {
              try {
                await form.validateFields();

                setLoading(true);
                const items: CountAddItem[] = tableData.filter(item => item.productCode).map((item: any) => ({
                  lotStr: item.lotStr || '',
                  productCode: item.productCode,
                  counting: item.counting?.toString() || '0',
                  barcodes: item.barcodes || [],
                }));

                if (countOrder) {
                  const itemIds = originalItems.map(item => item.id);
                  if (itemIds.length > 0) {
                    await countItemRms(countOrder.id, itemIds);
                  }
                  const updatedOrder = await countItemAdds(countOrder.id, items);
                  notification.success({ placement: 'top', message: t('counting.itemsUpdated') });
                  onClose();
                  onSuccess && onSuccess(updatedOrder, "ADD");
                } else {
                  const formValues = form.getFieldsValue();
                  const addData: CountAddProps = {
                    locationId: selectedLocation!.id,
                    desc: formValues.desc || '',
                    items,
                  };
                  const result = await countAdd(addData);
                  notification.success({ placement: 'top', message: t('counting.addSuccess') });
                  onClose();
                  onSuccess && onSuccess(result, "ADD");
                }
                setLoading(false);
              } catch (error: any) {
                setLoading(false);
                if (error === 73003) {
                  notification.error({ placement: 'top', message: t('counting.addFailed'), description: t('counting.preOutExt') });
                } else if (error?.errorFields) {
                  notification.error({ placement: 'top', message: t('common.validateFailed') });
                } else {
                  notification.error({ placement: 'top', message: t('counting.saveFailed') });
                }
              }
            },
            onCancel: async () => {
              if (countOrder) {
                try {
                  await countDel(countOrder.id);
                  onClose();
                } catch (err) {
                  onClose();
                }
              } else {
                onClose();
              }
            }
          });
        }
      }
    } else if (current === 2) {
      modal.confirm({
        title: t('counting.confirmTerminate'),
        icon: <ExclamationCircleOutlined />,
        content: t('counting.confirmTerminateContent'),
        okText: t('common.confirm'),
        cancelText: t('common.cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          if (countOrder) {
            try {
              await countDel(countOrder.id);
              notification.success({ placement: 'top', message: t('counting.terminateSuccess') });
              onClose(countId ? countOrder : undefined);
            } catch (err) {
              notification.error({ placement: 'top', message: t('counting.terminateFailed') });
            }
          } else {
            onClose();
          }
        },
        onCancel: () => {
          onClose();
          countOrder && onSuccess && onSuccess(countOrder, countId ? "UPDATE" : "ADD")
        }
      });
    } else {
      onClose();
      countOrder && onSuccess && onSuccess(countOrder, !!countId ? "UPDATE" : "ADD");
    }
  };

  const handleOpenBarcodeModal = (fieldIndex: number) => {
    const currentBarcodes = tableData[fieldIndex]?.barcodes || [];
    const countingValue = tableData[fieldIndex]?.counting;
    setBarcodeModalInfo({
      open: true,
      fieldIndex,
      barcodes: currentBarcodes,
      maxCount: countingValue || undefined
    });
  };

  const handleBarcodeModalOk = useCallback((barcodes: string[]) => {
    if (barcodeModalInfo.fieldIndex !== null) {
      if (tableData[barcodeModalInfo.fieldIndex]) {
        const currentItem = tableData[barcodeModalInfo.fieldIndex];
        const countingValue = currentItem.counting;

        if (countingValue && barcodes.length > countingValue) {
          notification.warning({
            placement: 'top',
            message: t('counting.barcodeExceedsCount'),
            description: t('counting.barcodeExceedsCountDesc', { max: countingValue })
          });
          return;
        }

        const newData = [...tableData];
        newData[barcodeModalInfo.fieldIndex] = { ...currentItem, barcodes };
        setTableData(newData);
        form.setFieldValue('items', newData);
        setFormChanged(true);
      }
    }
    setBarcodeModalInfo({ open: false, fieldIndex: null, barcodes: [] });
  }, [barcodeModalInfo, tableData, form, notification, t]);

  const handleBarcodeModalCancel = () => {
    setBarcodeModalInfo({ open: false, fieldIndex: null, barcodes: [] });
  };

  const genAllLot = () => {
    let resData: EditableTableItem[] = [...tableData];
    let now = Date.now();
    let fail: boolean = false;
    for (let i = 0; i < resData.length; i++) {
      let lot: string | undefined = undefined;
      if (resData[i].productCode) lot = generateLotV2(resData[i].productCode!, '', ++now);
      else fail = true;
      if (!!!resData[i].lotStr) resData[i].lotStr = lot
    };
    if (fail) notification.warning({ message: t('common.noCodeWarn') });
    setTableData([...resData]);
  };

  const diffColumns = [
    {
      title: t('product.code'),
      dataIndex: 'productCode',
      key: 'productCode',
      render: (text: string) => <Tag color="green">{text}</Tag>,
    },
    {
      title: t('product.name'),
      dataIndex: 'productName',
      key: 'productName',
    },
    {
      title: t('counting.lotStr'),
      dataIndex: 'lotStr',
      key: 'lotStr',
      render: (text: string) => text || '-',
    },
    {
      title: t('counting.systemCount'),
      dataIndex: 'count',
      key: 'count',
      render: (count: number) => <span>{count}</span>,
    },
    {
      title: t('counting.countingCount'),
      dataIndex: 'counting',
      key: 'counting',
      render: (counting: number, record: CountRes) => {
        const diff = counting - record.count;
        const color = diff === 0 ? 'green' : diff > 0 ? 'orange' : 'red';
        return (
          <Space>
            <span>{counting}</span>
            {diff !== 0 && <Tag color={color}>{diff > 0 ? `+${diff}` : diff}</Tag>}
          </Space>
        );
      },
    },
    {
      title: t('counting.barcodeDiff'),
      dataIndex: 'barcodeDiffs',
      key: 'barcodeDiffs',
      width: 180,
      render: (barcodeDiffs: BarcodeDiff[], record: CountRes) => {
        if (!barcodeDiffs || barcodeDiffs.length === 0) {
          return <Tag color="green">{t('counting.noDifference')}</Tag>;
        }
        return (
          <Space direction="vertical" size="small" align="center">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                setBarcodeDiffModal({
                  open: true,
                  barcodeDiffs,
                  productCode: record.productCode,
                  lotStr: record.lotStr,
                });
              }}
            >
              {t('counting.viewDiff')}
            </Button>
          </Space>
        );
      },
    },
  ];

  const editableColumns = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_: any, __: EditableTableItem, index: number) => index + 1,
    },
    {
      title: t('counting.lotStr'),
      dataIndex: 'lotStr',
      key: 'lotStr',
      width: 260,
      render: (_: any, record: EditableTableItem, index: number) => (
        <Input
          value={record.lotStr || ''}
          placeholder={t('counting.lotStr')}
          onChange={(e) => handleCellChange(index, 'lotStr', e.target.value)}
          status={record.lotStr ? undefined : 'error'}
          suffix={
            <Button onClick={() => {
              if (!!!record?.productCode) return notification.error({ message: t('product.plCode') })
              let lotStr = generateLotV2(record?.productCode ?? '', '', Date.now());
              handleCellChange(index, 'lotStr', lotStr)
            }} >{t('common.genLot')}</Button>
          }
        />
      ),
    },
    {
      title: t('product.code'),
      dataIndex: 'productCode',
      key: 'productCode',
      width: 200,
      render: (_: any, record: EditableTableItem, index: number) => (
        <Input
          value={record.productCode || ''}
          placeholder={t('product.code')}
          onChange={(e) => handleCellChange(index, 'productCode', e.target.value)}
          status={record.productCode ? undefined : 'error'}
        />
      ),
    },
    {
      title: t('counting.count'),
      dataIndex: 'counting',
      key: 'counting',
      width: 120,
      render: (_: any, record: EditableTableItem, index: number) => (
        <InputNumber
          value={record.counting || null}
          placeholder={t('counting.count')}
          min={0}
          onChange={(value) => handleCellChange(index, 'counting', value)}
          status={record.counting !== null && record.counting !== undefined ? undefined : 'error'}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t('common.opt'),
      key: 'operation',
      width: 120,
      render: (_: any, record: EditableTableItem, index: number) => (
        <Space size="small">
          <Button
            icon={<BarcodeOutlined />}
            onClick={() => handleOpenBarcodeModal(index)}
            disabled={!record.counting}
            title={t('counting.barcodes')}
          >
            {(record.barcodes?.length || 0)}
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteRow(index)}
            disabled={tableData.length <= 1}
            title={t('common.del')}
          />
        </Space>
      ),
    },
  ];

  const tableHeader = (
    <Flex gap={8}>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAddRow}
      >
        {t('counting.addItem')}
      </Button>
      <Upload
        accept=".xlsx,.xls"
        beforeUpload={handleImport}
        showUploadList={false}
        disabled={uploading}
      >
        <Button loading={uploading}>{t("counting.inputTemp")}</Button>
      </Upload>
      <Button onClick={() => generateTemplate(t)}>
        {t('counting.downloadTemplate')}
      </Button>
      <Button onClick={() => genAllLot()} type='dashed'>
        {t('common.genAllLot')}
      </Button>
    </Flex>
  );

  const steps = [
    {
      title: t('counting.selectLocation'),
      content: (
        <LocationSelectContent
          value={selectedLocation?.id}
          onChange={setSelectedLocation}
          disableLocked
        />
      ),
    },
    {
      title: t('counting.inputCountData'),
      content: (
        <Flex vertical style={{ height: "60vh" }}>
          {selectedLocation && (
            <Descriptions bordered>
              <Descriptions.Item label={t('warehouse.locationCode')}>
                <SText text={selectedLocation?.number} />
              </Descriptions.Item>
              <Descriptions.Item label={t('warehouse.labels')}>
                <Flex gap={4} wrap="wrap">
                  {selectedLocation.label1 && <Tag color={getLabelColor('label1')}>{selectedLocation.label1}</Tag>}
                  {selectedLocation.label2 && <Tag color={getLabelColor('label2')}>{selectedLocation.label2}</Tag>}
                  {selectedLocation.label3 && <Tag color={getLabelColor('label3')}>{selectedLocation.label3}</Tag>}
                  {selectedLocation.label4 && <Tag color={getLabelColor('label4')}>{selectedLocation.label4}</Tag>}
                  {selectedLocation.label5 && <Tag color={getLabelColor('label5')}>{selectedLocation.label5}</Tag>}
                </Flex>
              </Descriptions.Item>
            </Descriptions>
          )}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormSubmit}
            onValuesChange={() => setFormChanged(true)}
          >
            <Form.Item name="desc" label={t('common.desc')}>
              <Input.TextArea rows={2} placeholder={t('counting.inputDesc')} />
            </Form.Item>
          </Form>

          <STableLayout
            headerChildren={tableHeader}
            columns={editableColumns}
            dataSource={tableData}
            rowKey={(record, index) => `row-${index}`}
            total={tableData.length}
            pagination={false}
            enableColumnResize={false}
            scrollX={800}
            extHeight={100}
          />
        </Flex>
      ),
    },
    {
      title: t('counting.diffComparison'),
      content: (
        <div style={{ minHeight: '400px' }}>
          <Table
            loading={loading}
            columns={diffColumns}
            dataSource={diffData}
            rowKey={(record) => `${record.productId}-${record.lotStr}`}
            pagination={false}
            scroll={{ y: 400 }}
          />
        </div>
      ),
    },
    {
      title: t('counting.complete'),
      content: (
        <div style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={countOrder ? `${countOrder.number} ${t('counting.executeComplete')}` : t('counting.executeComplete')}
            subTitle={t('counting.executeSuccessDesc')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Flex gap={10} align="center" justify="flex-start">
            <SText text={t('counting.startInv')} style={{ width: "unset" }} />
          </Flex>
        }
        open={open}
        onCancel={handleClose}
        width={1200}
        style={{ top: 20 }}
        footer={
          <Flex style={{ width: "100%" }} justify="space-between">
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              {current < 3 ? (
                <Space>
                  {current > 0 && (
                    <Button onClick={handlePrev} disabled={current === 1 && !!countOrder}>{t('requirement.previous')}</Button>
                  )}
                  <Button
                    type="primary"
                    onClick={handleNext}
                    loading={loading}
                    disabled={current === 0 && !selectedLocation}
                  >
                    {t('requirement.next')}
                  </Button>
                </Space>
              ) : (
                <Button type="primary" onClick={handleClose}>
                  {t('common.confirm')}
                </Button>
              )}
            </div>
            <Button onClick={handleTempSave}>{t('counting.temp')}</Button>
          </Flex>
        }
      >
        <Steps current={current} items={steps.map(item => ({ title: item.title }))} style={{ marginBottom: 24 }} />
        {
          loading && current !== 2 ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <Spin size="large" />
            </div>
          ) : (
            steps[current].content
          )
        }
      </Modal>

      <BarcodeEditModal
        open={barcodeModalInfo.open}
        initialBarcodes={barcodeModalInfo.barcodes}
        createNum={barcodeModalInfo.maxCount}
        onOk={handleBarcodeModalOk}
        onCancel={handleBarcodeModalCancel}
      />

      <BarcodeDiffModal
        open={barcodeDiffModal.open}
        onClose={() => setBarcodeDiffModal({ ...barcodeDiffModal, open: false })}
        barcodeDiffs={barcodeDiffModal.barcodeDiffs}
        productCode={barcodeDiffModal.productCode}
        lotStr={barcodeDiffModal.lotStr}
      />
    </>
  );
};

export default CountAddV2;
